import { Sparkles, ShieldCheck, QrCode, Bell, LucideIcon } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { useTranslation } from "@/lib/i18n";

type FeatureConfig = {
  icon: LucideIcon;
  titleKey: string;
  descriptionKey: string;
  highlightKey: string;
};

const featureConfigs: FeatureConfig[] = [
  {
    icon: Sparkles,
    titleKey: "features.smarterReceipts",
    descriptionKey: "features.smarterReceiptsDesc",
    highlightKey: "features.noMoreCrypticCodes",
  },
  {
    icon: ShieldCheck,
    titleKey: "features.moreThanProof",
    descriptionKey: "features.moreThanProofDesc",
    highlightKey: "features.knowYourCoverage",
  },
  {
    icon: QrCode,
    titleKey: "features.readyToShow",
    descriptionKey: "features.readyToShowDesc",
    highlightKey: "features.everythingMerchantsNeed",
  },
  {
    icon: Bell,
    titleKey: "features.warrantyAlerts",
    descriptionKey: "features.warrantyAlertsDesc",
    highlightKey: "features.neverMissDeadline",
  },
];

export function Features() {
  const { t } = useTranslation();
  const featured = featureConfigs[0];
  const FeaturedIcon = featured.icon;

  return (
    <section id="features" className="py-20 sm:py-28 bg-secondary/30">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section heading */}
        <div className="max-w-3xl mx-auto text-center mb-16 animate-fade-up">
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-4">
            {t("features.refoundMakesReceiptsUseful").split(/{[^}]*}/)[0]}
            <span className="italic">{t("features.usefulAgain")}</span>
            {t("features.refoundMakesReceiptsUseful").split(/{[^}]*}/)[1] || ""}
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {t("features.featuresSubtitle")}
          </p>
        </div>

        {/* Bento grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-5xl mx-auto mb-6">
          {/* Featured card - full width */}
          <Card
            className="glass-card hover-lift group overflow-hidden border-t-2 border-transparent hover:border-primary/50 transition-all duration-300 lg:col-span-2 relative animate-fade-up stagger-1"
          >
            {/* Decorative gradient mesh in corner */}
            <div className="absolute top-0 right-0 w-64 h-64 pointer-events-none opacity-20 group-hover:opacity-30 transition-opacity duration-500">
              <div className="absolute inset-0 bg-primary/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/4" />
              <div className="absolute inset-0 bg-accent/15 rounded-full blur-2xl translate-y-1/4 translate-x-1/2" />
            </div>

            <CardContent className="p-8 sm:p-10 lg:p-12 relative z-10">
              <div className="flex flex-col sm:flex-row items-start gap-5">
                <div className="flex-shrink-0 w-14 h-14 rounded-lg bg-primary/10 text-primary flex items-center justify-center group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-300">
                  <FeaturedIcon className="w-7 h-7" />
                </div>
                <div className="flex-1 max-w-xl">
                  <span className="text-primary font-medium text-xs uppercase tracking-widest">
                    {t(featured.highlightKey)}
                  </span>
                  <h3 className="font-semibold text-xl sm:text-2xl text-foreground mt-2 mb-3">
                    {t(featured.titleKey)}
                  </h3>
                  <p className="text-muted-foreground text-base leading-relaxed">
                    {t(featured.descriptionKey)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Bottom 3 cards in a row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {featureConfigs.slice(1).map((feature, index) => {
            const Icon = feature.icon;
            return (
              <Card
                key={feature.titleKey}
                className="glass-card hover-lift group overflow-hidden border-t-2 border-transparent hover:border-primary/50 transition-all duration-300 animate-fade-up"
                style={{ animationDelay: `${(index + 2) * 0.08}s` }}
              >
                <CardContent className="p-6 sm:p-8">
                  <div className="flex flex-col items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-primary/10 text-primary flex items-center justify-center group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-300">
                      <Icon className="w-6 h-6" />
                    </div>
                    <div>
                      <span className="text-primary font-medium text-xs uppercase tracking-widest">
                        {t(feature.highlightKey)}
                      </span>
                      <h3 className="font-semibold text-lg text-foreground mt-2 mb-2">
                        {t(feature.titleKey)}
                      </h3>
                      <p className="text-muted-foreground text-sm leading-relaxed">
                        {t(feature.descriptionKey)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
}
